import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import StripeCheckoutForm from './components/StripeCheckoutForm';
import styles from './SubscribePage.module.css';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Context
import { AuthContext } from '../context/AuthContext';

// Utils
import { authenticatedFetch } from '../utils/authHelpers';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Price ID mapping for Stripe
const getPriceId = (tierName) => {
  const priceMap = {
    'INDIVIDUAL': process.env.REACT_APP_STRIPE_INDIVIDUAL_PRICE_ID,
    'TEAM': process.env.REACT_APP_STRIPE_TEAM_PRICE_ID,
    'ENTERPRISE': process.env.REACT_APP_STRIPE_ENTERPRISE_PRICE_ID,
  };
  return priceMap[tierName] || process.env.REACT_APP_STRIPE_INDIVIDUAL_PRICE_ID;
};

const tiers = [
  {
    name: 'INDIVIDUAL',
    price: '$50',
    docs: '20',
    icon: '/person-male-svgrepo-com.svg',
    features: [
      'Access to our AI Model capable of high accuracy and lightning fast extraction times',
      'Intuitive and capable upload dashboard',
      'Data export options: Simple and Full CSV, JSON, and PDF',
    ],
  },
  {
    name: 'TEAM',
    price: '$150',
    docs: '75',
    icon: '/group-svgrepo-com.svg',
    features: [
      'Access to our AI Model capable of high accuracy and lightning fast extraction times',
      'Intuitive and capable upload dashboard',
      'Data export options: Simple and Full CSV, JSON, and PDF',
    ],
  },
  {
    name: 'ENTERPRISE',
    price: '$500',
    docs: '300',
    icon: '/enterprise-svgrepo-com.svg',
    features: [
      'Access to our AI Model capable of high accuracy and lightning fast extraction times',
      'Intuitive and capable upload dashboard',
      'Data export options: Simple and Full CSV, JSON, and PDF',
    ],
  },
];

const sharedFeatures = [
  'Access to our AI Model capable of high accuracy and lightning fast extraction times',
  'Intuitive and capable upload dashboard',
  'Data export options: Simple and Full CSV, JSON, and PDF',
];

function SubscribePage() {
  const [selectedTier, setSelectedTier] = useState(null);
  const [currentUserTier, setCurrentUserTier] = useState(null);
  const [isChangePlanMode, setIsChangePlanMode] = useState(false);

  // Get current user from AuthContext
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Detect change-plan mode via query param and fetch current tier
  useEffect(() => {
    const fetchCurrentTier = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const changePlan = params.get('changePlan');
        setIsChangePlanMode(changePlan === '1');

        const response = await authenticatedFetch('/api/user/subscription-details');
        if (response.ok) {
          const details = await response.json();
          const { tier } = details;
          setCurrentUserTier(tier);

          // Only allow change-plan mode if user actually has a current tier
          if (changePlan === '1' && tier) {
            setIsChangePlanMode(true);
          } else {
            setIsChangePlanMode(false);
          }
        } else {
          setIsChangePlanMode(false);
        }
      } catch (error) {
        if (error.message.includes('New user - no subscription document')) {
          // New user - no graying out needed
          console.log('New user - no existing subscription');
          setCurrentUserTier(null);
        } else {
          // Other errors - handle appropriately
          console.error('Failed to fetch subscription details:', error);
          setCurrentUserTier(null);
        }
      }
    };
    
    fetchCurrentTier();
  }, [location.search]);

  const handlePaymentSuccess = () => {
    console.log('Payment successful for tier:', selectedTier?.name);
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
  };

  const handleCancel = () => {
    const isAuthed = !!currentUser;
    const isNewUser = currentUserTier === null;
    if (!isAuthed || isNewUser) {
      navigate('/login');
    } else {
      navigate('/upload');
    }
  };

  return (
    <main className={styles.subscribePage}>
      {/* Navbar */}
      <header className={styles.subscribeNavbar}>
        <div className={styles.subscribeNavbarContent}>
          <img
            className={styles.subscribeLogo}
            alt="GigemAI Logo"
            src="gigemai_logo_v2.png"
          />
        </div>
      </header>

      {/* Main Content */}
      <section className={styles.subscribeSplitContainer}>
        <div className={styles.plansContainer}>
          {/* Top Wide Card */}
          <div className={styles.wideCard}>
            <div className={styles.wideCardTopBar}>
              <span className={styles.wideCardTopBarLabel}>Choose Your Plan Rate</span>
              <div className={styles.wideCardCaret}>
                <svg viewBox="0 0 24 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 0L12 12L24 0H0Z" fill="var(--color-navbar)"/>
                </svg>
              </div>
            </div>
            <div className={styles.wideCardContent}>
              <div className={styles.featuresSection}>
                <ul className={styles.featureList}>
                  {sharedFeatures.map((feature, idx) => (
                    <li key={idx} className={styles.featureItem}>
                      <CheckCircleIcon className={styles.checkIcon} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={styles.verticalDivider}></div>
              <div className={styles.billingSection}>
                <div className={styles.billingText}>Billed Monthly</div>
                <div className={styles.billingText}>For custom rates, contact <span className={styles.billingEmail}>sales@gigemai.com</span></div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <hr className={styles.plansDivider} />

          {/* Sub Tier Cards */}
          <div className={styles.subscriptionBoxRow}>
            {tiers.map((tier) => {
              // Check if this tier should be disabled
              const shouldDisable = currentUserTier === 'admin' || 
                                   (currentUserTier && tier.name.toLowerCase() === currentUserTier.toLowerCase());
              
              return (
                <div
                  className={
                    selectedTier?.name === tier.name
                      ? `${styles.subscriptionBox} ${styles.subscriptionBoxSelected}`
                      : shouldDisable
                      ? `${styles.subscriptionBox} ${styles.subscriptionBoxDisabled}`
                      : styles.subscriptionBox
                  }
                  key={tier.name}
                  onClick={() => {
                    if (shouldDisable) return; // Prevent selection of disabled tiers
                    
                    if (selectedTier?.name === tier.name) {
                      setSelectedTier(null);
                    } else {
                      setSelectedTier(tier);
                    }
                  }}
                tabIndex={0}
                role="button"
                aria-pressed={selectedTier?.name === tier.name}
              >
                <div className={styles.subscriptionTopBar}>
                  <span className={styles.subscriptionTopBarLabel}>{tier.name}</span>
                  <div className={styles.subscriptionTopBarCaret}>
                    <svg viewBox="0 0 24 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 0L12 12L24 0H0Z" fill="var(--color-navbar)"/>
                    </svg>
                  </div>
                </div>
                <div className={styles.subscriptionTextRow}>
                  <div className={styles.subscriptionTextCol}>
                    <span className={styles.subscriptionPrice}>{tier.price}</span>
                    <span className={styles.subscriptionSubLabel}>per month</span>
                  </div>
                  <div className={styles.subscriptionTextCol}>
                    <span className={styles.subscriptionDocCount}>{tier.docs}</span>
                    <span className={styles.subscriptionSubLabel}>Documents</span>
                  </div>
                </div>
                <img
                  src={tier.icon}
                  alt={`${tier.name} Icon`}
                  className={styles.subscriptionBgIcon}
                  draggable={false}
                />
                <div className={styles.subscriptionFor}>FOR</div>
              </div>
              );
            })}
          </div>
        </div>

        {/* Right: Stripe Checkout Form */}
        <div className={styles.formSection}>
          <div className={styles.subscribeFormGlass}>
            <Elements stripe={stripePromise}>
              <StripeCheckoutForm
                selectedTier={selectedTier}
                userEmail={currentUser.email}
                userId={currentUser.uid}
                getPriceId={getPriceId}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                onCancel={handleCancel}
                isChangePlanMode={isChangePlanMode}
                currentUserTier={currentUserTier}
              />
            </Elements>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <span className={styles.footerText}>GigemAI</span>
      </footer>
    </main>
  );
}

export default SubscribePage; 